import type { PrescriptionDeepState } from './prescription/prescriptionState';
import type { SolutionSubtype } from './SolutionSubtypePicker';

/**
 * Prescription reuse rules, kept in their own module so they can be unit-tested
 * without importing PriorOrderPickerModal (which pulls in `@/lib/supabase`
 * eagerly). Same reasoning as prescriptionFormDefaults.
 *
 * ===========================================================================
 * CROSS-TYPE REUSE IS ALLOWED (2026-08-06). WHY THE OLD REFUSAL WAS WRONG.
 * ===========================================================================
 * This module used to require an EXACT subtype match and refuse everything else
 * with "Different product type". The stated reason was that cross-type mapping
 * had no machine-checkable source of truth.
 *
 * That reason had stopped being true, and the refusal had stopped being a
 * safety property. Two things changed underneath it:
 *
 *  1. THE OTHER ROUTE ALREADY DID IT. The patient-step reorder search
 *     (PatientPicker's onPick -> buildReorderPrescriptionPatch) copies a past
 *     prescription onto whatever product the wizard has selected, with NO
 *     subtype filter on the search and no subtype check on the copy. So the
 *     capability was live; it just sat behind the path that does not advertise
 *     it, while the button actually labelled "Use this prescription" refused.
 *     A rule enforced on one of two doors is not a rule, it is a coin toss
 *     decided by which door the operator happened to use.
 *
 *  2. THE LANDING SIDE GREW A CHECK. collectUnavailableValues (PR #120) reports
 *     every carried value the target does not offer, the red banner names them,
 *     and canProceedFromStepWithReason + isCustomDraftComplete both block on
 *     them. That is the protection the reorder route has always relied on, and
 *     it does not care which route delivered the value.
 *
 * Rafael, 2026-08-06: let the button do what the search already does, and rely
 * on the banner and the submit block, which is exactly what already protects
 * the other route.
 *
 * So the two routes now share ONE rule, stated once here and mirrored in
 * buildReorderPrescriptionPatch: intersect by TARGET, never by source.
 *
 *   rearfoot / midfoot / forefoot / offloadings  every insole and sandal
 *                                                renders these, so they always
 *                                                carry
 *   design                                       insoles only
 *   sandal                                       sandals only
 *   (nothing)                                    refurbishment renders no
 *                                                prescription cards at all
 *
 * The SOURCE subtype is not consulted for any of that, deliberately: what the
 * target renders is the only thing that decides what is carryable. It is read
 * once, for the row's note, so the operator is told what will not come across.
 *
 * ===========================================================================
 * WHAT THIS DELIBERATELY DOES NOT DO
 * ===========================================================================
 * No value TRANSLATION. A rearfoot wedge type that exists on a PA12 insole and
 * not on a sandal is carried across as-is and then flagged; it is not silently
 * rewritten into the nearest sandal equivalent. Choosing the replacement is a
 * clinical decision and stays with the podiatrist. The one exception is a field
 * whose target offers exactly one option, resolved by applySoleOptions at the
 * copy point, where there is no judgement to make. The shared mapping module
 * (backlog item 5) is closed as unnecessary: this unification plus the existing
 * checker is what item 5 was for.
 */

/** Regions every insole AND sandal renders, so they carry to any target. */
export const SHARED_DEEP_REGIONS = [
  'rearfoot',
  'midfoot',
  'forefoot',
  'offloadings',
] as const;

/**
 * Product-specific regions, with the card title the operator actually sees on
 * the form. The note has to name the thing they will go looking for, not the
 * JSONB key.
 */
export const REGION_LABELS = {
  design: 'Insole design',
  sandal: 'Sandal style',
} as const;

/** Every region reuse can carry, shared plus product-specific. */
export const REUSABLE_DEEP_REGIONS = [
  ...SHARED_DEEP_REGIONS,
  'design',
  'sandal',
] as const;

export interface ReuseOk {
  ok: true;
  deep: PrescriptionDeepState;
  observations: string | undefined;
  /**
   * Card titles present in the source that this target will NOT render, so the
   * row can say so before the operator commits. Empty when everything carried.
   */
  dropped: readonly string[];
  /** Source product is known and differs from the target. Note copy only. */
  crossType: boolean;
}

export type ReuseVerdict = ReuseOk | { ok: false; reason: string };

export interface ReuseCandidateRow {
  primary_solution_subtype?: string | null;
  primary_configuration?: Record<string, unknown> | null;
}

/** A plain, non-array object. Arrays are `typeof 'object'` too. */
function isPlainNode(n: unknown): n is Record<string, unknown> {
  return !!n && typeof n === 'object' && !Array.isArray(n);
}

/**
 * Deep copy, so an applied prescription cannot alias the react-query cache.
 *
 * buildReorderPrescriptionPatch has always cloned; this module used to assign
 * the source nodes BY REFERENCE. The rows come straight out of the patient
 * line-history query cache, so editing a copied rearfoot card wrote back into
 * the cached row, and reopening the modal showed the operator's in-progress
 * edits as if they were the past order's prescription. Cross-type reuse carries
 * more nodes down more paths, so unify on the safe one.
 */
function clonePlain<T>(v: T): T {
  if (typeof structuredClone === 'function') return structuredClone(v);
  return JSON.parse(JSON.stringify(v)) as T;
}

/**
 * THE shared rule. Intersect a source `deep` against what the target product
 * actually renders, and report what was left behind.
 *
 * Both copy routes call this: evaluateReuse (the "Use this prescription"
 * button) and buildReorderPrescriptionPatch (the patient-step reorder search).
 * They used to hold two separate implementations of the same idea, which is how
 * they came to disagree about whether cross-type reuse was allowed at all. One
 * function is a stronger guarantee than a test asserting two agree, because a
 * test only fails after somebody has already changed one of them.
 *
 * Refurbishment renders no prescription cards, so it carries nothing. What each
 * caller DOES about an empty result differs and stays with the caller: the
 * button refuses out loud (it is a button, it can be disabled with a reason),
 * the reorder prefill just leaves the prescription at its defaults (there is no
 * per-row affordance there to refuse).
 */
export function intersectDeepByTarget(
  srcDeep: Record<string, unknown>,
  target: SolutionSubtype | null | undefined,
  /**
   * The SOURCE product, used ONLY to decide whether a dropped region is worth
   * mentioning. It never affects what carries.
   *
   * Why it is needed: the wizard seeds DEFAULT_PRESCRIPTION_STATE.deep onto
   * every line, design node included, so a SANDAL line persists a `design`
   * node it never rendered a card for. Without this, a sandal-to-sandal reuse
   * reported "Insole design will not carry" about a node holding nothing but
   * defaults. Measured 2026-08-06: 0 of the 97 sandal lines from May carry a
   * design node, but 4 of the last 5 do, so this was about to fire on nearly
   * every sandal reuse.
   *
   * Unknown source (45 live lines) stays conservative and REPORTS, because we
   * cannot prove the region was only a seeded default.
   */
  sourceSubtype?: string | null | undefined,
): { deep: Record<string, unknown>; dropped: string[] } {
  const deep: Record<string, unknown> = {};
  const dropped: string[] = [];
  if (target === 'insoles_refurbishment') return { deep, dropped };

  const targetIsSandal = target === 'orthopaedic_sandal';

  for (const k of SHARED_DEEP_REGIONS) {
    const node = srcDeep[k];
    if (isPlainNode(node)) deep[k] = clonePlain(node);
  }

  // Product-specific regions: carried only onto the product that renders them,
  // and REPORTED when they are not, because "we quietly dropped your sandal
  // style" and "your sandal style came across" look identical on a form whose
  // sections are all collapsed.
  // A region is only worth REPORTING as dropped when the source product
  // actually rendered a card for it. Anything else is a seeded default the
  // operator never saw, and warning about it trains them to ignore the warning.
  const sourceKnown = !!sourceSubtype;
  const sourceIsSandal = sourceSubtype === 'orthopaedic_sandal';
  const sourceRenderedDesign =
    !sourceKnown || (!sourceIsSandal && sourceSubtype !== 'insoles_refurbishment');
  const sourceRenderedSandal = !sourceKnown || sourceIsSandal;

  if (isPlainNode(srcDeep['design'])) {
    if (targetIsSandal) {
      if (sourceRenderedDesign) dropped.push(REGION_LABELS.design);
    } else deep['design'] = clonePlain(srcDeep['design']);
  }
  if (isPlainNode(srcDeep['sandal'])) {
    if (targetIsSandal) deep['sandal'] = clonePlain(srcDeep['sandal']);
    else if (sourceRenderedSandal) dropped.push(REGION_LABELS.sandal);
  }

  return { deep, dropped };
}

/**
 * Decide whether a prior order can seed this line's prescription, and refuse
 * with a SPECIFIC reason when it cannot. Every refusal path here previously
 * ended up either silently blanking the form or reporting the wrong cause:
 *
 *  * a row whose configuration carries no usable `deep` produced an empty patch,
 *    and an empty patch means a FULL RESET at the wizard, so a button labelled
 *    "Use this prescription" wiped the prescription instead of applying one;
 *  * refurbishment renders no prescription cards at all, so carrying `deep` onto
 *    it leaves values the operator can never see or correct. The reorder path
 *    already strips these (buildReorderPrescriptionPatch's targetIsRefurb); this
 *    path must strip them too rather than diverge from it.
 *
 * Note what is NO LONGER a refusal: a source whose product differs from the
 * target, and a source whose product was never recorded (45 live lines). Both
 * used to be refused here and neither was ever refused on the reorder route.
 */
export function evaluateReuse(
  r: ReuseCandidateRow,
  target: SolutionSubtype | null | undefined,
): ReuseVerdict {
  if (!target) return { ok: false, reason: 'Pick a product type first' };
  if (target === 'insoles_refurbishment') {
    return { ok: false, reason: 'Refurbishment has no prescription' };
  }

  const cfg = (r.primary_configuration ?? {}) as Record<string, unknown>;
  const srcDeep = isPlainNode(cfg['deep']) ? cfg['deep'] : {};

  const { deep, dropped } = intersectDeepByTarget(
    srcDeep,
    target,
    r.primary_solution_subtype,
  );

  if (Object.keys(deep).length === 0) {
    // Distinguish "there was nothing" from "there was something and none of it
    // fits", because the second is a real answer about THIS pairing and the
    // first is a fact about the source order.
    return {
      ok: false,
      reason:
        dropped.length > 0
          ? 'Nothing here fits this product'
          : 'No prescription details stored',
    };
  }

  const observations =
    typeof cfg.observations === 'string' && cfg.observations.trim()
      ? cfg.observations
      : undefined;

  return {
    ok: true,
    deep: deep as PrescriptionDeepState,
    observations,
    dropped,
    crossType:
      !!r.primary_solution_subtype && r.primary_solution_subtype !== target,
  };
}

/**
 * One line telling the operator what this reuse will and will not bring across,
 * or null when there is nothing worth saying (a same-product reuse that carries
 * everything, which is the old behaviour and needs no explanation).
 *
 * Deliberately does NOT name the target product. The subtype display names are
 * i18n keys resolved in SolutionSubtypePicker, and the local fallback renders
 * `pa12_insole` as "Pa12 Insole", which reads like a bug in a sentence meant to
 * build confidence.
 *
 * AND IT MUST NOT PROMISE FLAGGING. The first draft ended "anything this
 * product does not offer is flagged after you apply it", which is FALSE on a
 * reachable and entirely ordinary path, found by review before this shipped:
 *
 *   ForefootSection seeds an intrinsic Morton extension to softness 'rigid' on
 *   every non-sandal. Copy that insole onto a sandal and the TYPE ('intrinsic')
 *   is legal there, so collectUnavailableValues reports nothing; the softness
 *   cascade is explicitly outside its scope (see forefootRules). The banner
 *   does not render, submit is not blocked, and prescriptionExcelExport writes
 *   the Morton extension unconditionally, so "softness/rigidity: Rigid" reaches
 *   a sandal lab sheet whose form only offers semi-rigid.
 *
 * The manufacturing outcome is not new (the reorder search already carried
 * forefoot cross-type), but telling the operator it would be caught IS new, and
 * a false assurance on a manufacturing path is worse than no assurance: it is
 * the reason they would not check.
 *
 * So the note says what is TRUE and useful, which is: go and look.
 */
export function reuseNote(v: ReuseOk): string | null {
  const willNotCarry =
    v.dropped.length > 0 ? `${v.dropped.join(' and ')} will not carry` : null;

  if (!v.crossType) return willNotCarry ? `${willNotCarry}.` : null;

  const head = 'From a different product.';
  const tail = 'Open each section and review it before submitting.';
  return willNotCarry ? `${head} ${willNotCarry}. ${tail}` : `${head} ${tail}`;
}
